// Prose — the long-form reading container, pure.
//
// This is the MATERIAL reading face: the surface a learner reads authored
// material on, and the surface the agent's longer replies render on. It
// renders a markdown STRING into React elements — parsing is its whole job.
// Distinct from a chat bubble (that was v0's register): here the type is the
// reading face (--font-reading), the rhythm is relaxed (--leading-relaxed),
// the line-length is capped at the comfortable measure (--measure-reading),
// and the heading ramp is a full document ramp (30/24/20/16), not a
// bubble's compressed one. The material reads like a document, not a form.
//
// react-markdown renders to React elements, never innerHTML, so raw HTML in
// the model can't inject script — XSS-safe by construction, no sanitiser
// needed, and we deliberately do NOT enable raw-HTML passthrough. remark-gfm
// adds tables / task-lists / strikethrough. No sanitisation policy is
// decided here beyond react-markdown's safe defaults.
//
// Line breaks follow the chat convention every LLM writes to, not strict
// CommonMark (bd-nag): a single newline is a visible break (remark-breaks),
// and a numbered list may start right under a line of text at any number
// (`separateListStarts`). CommonMark lets only a list starting at 1 interrupt
// a paragraph, so "**Part II**\n3. Roots\n4. ..." used to collapse into one
// run-on paragraph while "**Part I**\n1. ..." rendered as a list.
//
// Pure/props-driven: the only input is the markdown source. No state, no
// effects, no I/O, no domain knowledge. Every type / space / colour comes
// from tokens via Prose.css, scoped to .bilda-prose so it never leaks into
// the surrounding chrome. There is no variant/size/state axis: a reading
// container has one job and one look, so a single named prop type — not a
// discriminated union — is the honest shape.

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import "./Prose.css";

// One markdown link as the renderer hands it over: the raw href + its rendered
// text. A caller that knows what in-app hrefs mean (chat: resource routes →
// chips) supplies a `renderLink`; without one every link is external.
export type ProseLinkProps = {
    readonly href: string | undefined;
    readonly children: ReactNode;
};
export type RenderProseLink = (link: ProseLinkProps) => ReactNode;

export type ProseProps = {
    // The markdown source. A plain string, not pre-parsed — parsing is this
    // component's whole job.
    readonly children: string;
    readonly renderLink?: RenderProseLink;
    // Flow inside a line of surrounding text (a recap sentence): renders a
    // span, paragraphs collapse to their text, and type/measure are inherited.
    readonly inline?: boolean;
};

// Links leave the app: open in a new tab and sever the opener reference
// (reverse-tabnabbing guard, SC-safe). The visible channel is the underline,
// not colour — the accent is reserved for the primary action / agent identity
// and is not spent on body links.
export const ExternalLink = ({ href, children }: ProseLinkProps) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
    </a>
);

// Where a markdown image may load from. Agent replies render here, so an image
// URL would load the moment the reply shows: a prompt injection could make the
// agent write one that carries workspace data to any server, no click needed
// (#287). Only a path on this origin loads ("inline"); an absolute URL becomes
// a plain link ("link"), so nothing leaves until the reader clicks. An empty
// src (react-markdown's urlTransform blanks data:, blob:, javascript:, ...)
// keeps only its alt text. Paths resolve against a placeholder origin, so
// "//host" and "/\host" count as other hosts.
export type ProseImage =
    | { readonly type: "inline"; readonly src: string }
    | { readonly type: "link"; readonly href: string }
    | { readonly type: "text" };

const PLACEHOLDER_ORIGIN = "https://app.invalid";

export const classifyImage = (src: unknown): ProseImage => {
    if (typeof src !== "string" || src === "") return { type: "text" };
    return URL.parse(src, PLACEHOLDER_ORIGIN)?.origin === PLACEHOLDER_ORIGIN
        ? { type: "inline", src }
        : { type: "link", href: src };
};

const FENCE = /^ {0,3}(```|~~~)/;
const LIST_ITEM = /^ {0,3}([-*+]|\d{1,9}[.)])[ \t]/;
const ORDERED_ITEM = /^ {0,3}\d{1,9}[.)][ \t]/;

// Pure: insert a blank line before an ordered-list item that directly follows
// a plain text line, so the list starts whatever its first number. Lines that
// are already list items, indented continuations, or inside a code fence are
// left alone.
export const separateListStarts = (markdown: string): string =>
    markdown
        .split("\n")
        .reduce<{ readonly out: readonly string[]; readonly fenced: boolean }>(
            ({ out, fenced }, line) => {
                if (FENCE.test(line)) {
                    return { out: [...out, line], fenced: !fenced };
                }
                const prev = out.at(-1);
                const startsList =
                    !fenced &&
                    ORDERED_ITEM.test(line) &&
                    prev !== undefined &&
                    prev.trim() !== "" &&
                    !/^\s/.test(prev) &&
                    !LIST_ITEM.test(prev);
                return {
                    out: startsList ? [...out, "", line] : [...out, line],
                    fenced,
                };
            },
            { out: [], fenced: false },
        )
        .out.join("\n");

export const Prose = ({ children, renderLink, inline = false }: ProseProps) => {
    const { t } = useLingui();

    // Element overrides live inside the component so the task-list labels can
    // localise via the `t` macro (a module-scope map couldn't). Every other
    // element uses react-markdown's default mapping, styled by Prose.css.
    const components: Components = {
        a: ({ children, href }) =>
            renderLink === undefined ? (
                <ExternalLink href={href}>{children}</ExternalLink>
            ) : (
                renderLink({ href, children })
            ),
        img: ({ src, alt }) => {
            const image = classifyImage(src);
            switch (image.type) {
                case "inline":
                    return <img src={image.src} alt={alt ?? ""} />;
                case "link":
                    return (
                        <ExternalLink href={image.href}>
                            {alt || image.href}
                        </ExternalLink>
                    );
                case "text":
                    return <>{alt}</>;
                default:
                    return exhaustSwitchCase(image);
            }
        },
        ...(inline ? { p: ({ children }) => <>{children}</> } : {}),
        // GFM task-list checkboxes render label-less by default, which axe
        // flags. Give each an accessible name for its state. defaultChecked
        // (uncontrolled) + disabled shows the state statically without a
        // controlled-input warning. Markdown only ever emits checkbox inputs,
        // so other input types don't occur — returning null for them is safe.
        input: ({ type, checked }) =>
            type === "checkbox" ? (
                <input
                    type="checkbox"
                    defaultChecked={checked ?? false}
                    disabled
                    aria-label={checked ? t`completed` : t`not completed`}
                />
            ) : null,
    };

    const markdown = (
        <Markdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={components}
        >
            {separateListStarts(children)}
        </Markdown>
    );
    return inline ? (
        <span className="bilda-prose bilda-prose--inline">{markdown}</span>
    ) : (
        <div className="bilda-prose">{markdown}</div>
    );
};
